import type { paths } from '@luxuryescapes/contract-svc-merchandise'
import { merchandiseCacheKey } from 'components/Merchandise/merchandiseUtils'

type Response = paths['/api/merchandise/adverts/{id}']['get']['responses']['200']['content']['application/json']
type ServerMerchandise = NonNullable<Response['result']['advert']>

interface ServerSeller {
  id: string
  businessName: string
  emailAddress: string
  returnPolicy?: string | null
  shippingPolicy?: string | null
  servicePolicy?: string | null
  shippingSLA?: { minimum: number; maximum: number }
  domesticShippingFreeThreshold?: number | null
}

interface GoldenAdvertVariantFields {
  sellerId?: string
  sellerName?: string
  advertId?: string
}

function mapSeller(seller: ServerSeller): App.MerchandiseOfferSeller {
  return {
    id: seller.id,
    name: seller.businessName,
    emailAddress: seller.emailAddress,
    shipping: {
      minDays: seller.shippingSLA?.minimum ?? 0,
      maxDays: seller.shippingSLA?.maximum ?? 0,
    },
    returnPolicy: seller.returnPolicy ?? undefined,
    shippingPolicy: seller.shippingPolicy ?? undefined,
    servicePolicy: seller.servicePolicy ?? undefined,
    domesticShippingFreeThreshold: seller.domesticShippingFreeThreshold ?? undefined,
  }
}

function mapMerchandiseVariant(
  variant: ServerMerchandise['variants'][number],
  images: Array<App.Image>,
): App.MerchandiseOfferVariant {
  const goldenVariant = variant as ServerMerchandise['variants'][number] & GoldenAdvertVariantFields

  let variantImages = images
  if (variant.images.length > 0) {
    variantImages = variant.images.map((image): App.Image => ({
      id: image.imageId,
      title: image.alt ?? undefined,
    }))
  }

  const mappedOptions = variant.variantOptionValues
    .filter(option => option.optionValue)
    .map((option): App.MerchandiseOfferOption | App.MerchandiseOfferColourOption => {
      const baseOption = {
        id: option.id,
        type: option.optionType.displayName,
        name: option.optionValue!.displayName,
        displayType: option.optionType.displayType ?? 'text',
        position: option.optionValue!.position,
      }

      if (option.optionType.displayType === 'colour') {
        return {
          ...baseOption,
          colour: option.optionValue!.hex ?? undefined,
        }
      }

      return baseOption
    })

  return {
    id: variant.id,
    sku: variant.sku ?? undefined,
    label: variant.label,
    maxPurchaseQuantity: variant.maxPurchaseQuantity ?? undefined,
    minPurchaseQuantity: variant.minPurchaseQuantity ?? undefined,
    inventory: variant.infiniteQuantity ? 9999 : (variant.countOnHand ?? 0),
    price: variant.price,
    regularPrice: variant.basePrice,
    pointsPrice: variant.pointPrice,
    pointsRegularPrice: variant.pointBasePrice,
    options: mappedOptions,
    images: variantImages,
    shippingProfileId: variant.shippingProfile?.id,
    shippingProfile: variant.shippingProfile ? {
      id: variant.shippingProfile.id,
      name: variant.shippingProfile.name,
      rates: variant.shippingProfile.rates,
    } : undefined,
    loyalty: {
      productType: variant.loyalty.productType,
      earnKey: variant.loyalty.earnKey,
    },
    mx: variant.mx,
    sellerId: goldenVariant.sellerId,
    sellerName: goldenVariant.sellerName,
    advertId: goldenVariant.advertId,
    pointsEarnRatio: variant.pointsEarnRatio ?? undefined,
  }
}

function mapSizeChart(sizeChart: ServerMerchandise['sizeMatrix']): App.MerchandiseOfferSizing | undefined {
  if (!sizeChart) {
    return undefined
  }

  if (typeof sizeChart.data === 'string') {
    return {
      image: {
        id: sizeChart.data,
      },
    }
  } else {
    return {
      charts: Object.entries(sizeChart.data).map(([name, sizeOptions]): App.MerchandiseOfferSizeChart => {
        return {
          name,
          headings: Object.keys(sizeOptions[0]),
          rows: sizeOptions.map((sizeOption) => Object.values(sizeOption)),
        }
      }),
    }
  }
}

function mapMerchandiseOffer(serverMerchandise: ServerMerchandise, prioritisedVariantIds?: Array<string>): App.MerchandiseOffer {
  const images: Array<App.Image> = []

  if (serverMerchandise.imageId) {
    images.push({ id: serverMerchandise.imageId })
  }

  if (serverMerchandise.images.length > 0) {
    images.push(...serverMerchandise.images.map((image): App.Image => ({
      id: image.imageId,
      title: image.alt ?? undefined,
    })))
  }
  const variants = serverMerchandise.variants.map((variant) => mapMerchandiseVariant(variant, images)).sort((a, b) => {
    const aPrio = prioritisedVariantIds?.includes(a.id)
    const bPrio = prioritisedVariantIds?.includes(b.id)
    if (aPrio && bPrio) return 0
    if (aPrio) return -1
    if (bPrio) return 1
    return 0
  })

  return {
    type: 'merchandise',
    parentType: 'merchandise',
    analyticsType: 'merchandise',
    productType: 'merchandise',
    loyalty: {
      // At the moment there's no concept of an offer level loyalty product type for shop however this may change in the future
      productType: 'shop_tier_2',
      earnRatio: 3,
    },
    id: serverMerchandise.id,
    slug: serverMerchandise.slug ?? '',
    seller: mapSeller(serverMerchandise.seller),
    luxPlus: {},
    name: serverMerchandise.name,
    category: serverMerchandise.category ? {
      id: serverMerchandise.category.id,
      slug: serverMerchandise.category.slug,
      name: serverMerchandise.category.displayName,
      parentId: serverMerchandise.category.parentId ?? undefined,
    } : undefined,
    brand: {
      id: serverMerchandise.brand!.id,
      name: serverMerchandise.brand!.name,
    },
    images,
    variants,
    copy: {
      description: serverMerchandise.description ?? undefined,
      features: serverMerchandise.productFeatures ?? undefined,
      specifications: serverMerchandise.specifications ?? undefined,
      shippingPolicy: serverMerchandise.seller.shippingPolicy ?? undefined,
      returnPolicy: serverMerchandise.seller.returnPolicy ?? undefined,
    },
    sizing: mapSizeChart(serverMerchandise.sizeMatrix),
    hasFreeShipping: serverMerchandise.hasFreeShipping,
    cacheKey: merchandiseCacheKey(serverMerchandise.id, prioritisedVariantIds),
  }
}

export default mapMerchandiseOffer
